import { Link } from "react-router-dom";
import { LeaderboardPlayer, LeaderboardTeam } from "../../types/leaderboard";
import { placementTier } from "../../utils/leaderboard/rank";
import { sortPlayersByRole } from "../../utils/leaderboard/roles";
import { getPlayerProfilePath } from "../../utils/playerProfilePath";
import { TeamLogo } from "../shared/TeamLogo";
import { OpggRankCell } from "./OpggRankCell";
import { PlayerExternalLinks } from "./PlayerExternalLinks";
import { PlayerNameDisplay } from "./PlayerNameDisplay";
import { PlayerRoleBadge } from "./PlayerRoleBadge";
import { ProfileIcon } from "./ProfileIcon";
import { RosterStatusBadge } from "./RosterStatusBadge";
import { WinratePills } from "./WinratePills";
import { useLeaderboardTabs, type LeaderboardTab } from "./hooks/useLeaderboardTabs";

interface LeaderboardTabsProps {
  teams: LeaderboardTeam[];
  loading?: boolean;
  onTabChange?: (tab: LeaderboardTab) => void;
}

/** Top three placements get the podium gold used across the board. */
const rankNumClass = (position: number) =>
  position <= 3 ? "font-bold text-[#e8c872]" : "font-semibold text-[#8b8b9a]";

const SKELETON_BLOCK =
  "animate-skeleton-shimmer rounded-lg bg-[linear-gradient(90deg,#2a2a36_25%,#343444_50%,#2a2a36_75%)] [background-size:200%_100%]";

const TOOLBAR_TAB = "fh-ghost-btn px-4 py-[0.45rem] text-[0.88rem]";

const TOOLBAR_SELECT =
  "cursor-pointer appearance-none rounded-md border border-[var(--surface-card-border)] bg-[#111118] py-[0.4rem] pl-[0.7rem] pr-8 text-[0.84rem] font-semibold text-[#e8e8ef] [background-image:var(--select-caret)] [background-position:right_0.6rem_center] [background-repeat:no-repeat] focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-[rgba(96,165,250,0.35)] max-[900px]:max-w-[14rem] max-[900px]:flex-1";

const TOOLBAR_LABEL = "text-[0.78rem] font-semibold uppercase tracking-[0.04em] text-[#8b8b9a]";

/** Player rows stack on mobile: rank + winrate become full-width scroll strips. */
const MOBILE_SCROLL_CELL =
  "max-[900px]:col-span-full max-[900px]:w-full max-[900px]:max-w-full max-[900px]:justify-self-stretch max-[900px]:overflow-x-auto max-[900px]:[-webkit-overflow-scrolling:touch] max-[900px]:[scrollbar-width:thin]";

const RANK_CELL_CLASS = `w-[10.75rem] justify-self-end ${MOBILE_SCROLL_CELL} max-[900px]:row-start-2 max-[900px]:pl-[0.15rem] max-[900px]:[&_.opgg-rank-cell]:w-max max-[900px]:[&_.opgg-rank-cell]:min-w-full`;

/** Nested roster rows right-align the rank block against the winrate column. */
const ROSTER_RANK_CELL_CLASS = `${RANK_CELL_CLASS} [&_.opgg-rank-cell]:justify-end [&_.opgg-rank-text]:items-end [&_.opgg-rank-text]:text-right`;

const WINRATE_CELL_CLASS = `justify-self-end ${MOBILE_SCROLL_CELL} max-[900px]:row-start-3 max-[900px]:[&_.opgg-winrate]:w-max max-[900px]:[&_.opgg-winrate]:min-w-full max-[900px]:[&_.opgg-winrate]:flex-nowrap`;

const EXTERNAL_LINKS_CLASS =
  "self-center justify-self-end max-[900px]:col-start-2 max-[900px]:row-start-1 max-[900px]:z-[1] max-[900px]:self-start max-[900px]:justify-self-end max-[900px]:pt-[0.1rem]";

const PLAYER_ENTITY_CLASS =
  "flex w-full min-w-0 items-center justify-start justify-self-start max-[900px]:col-start-2 max-[900px]:row-start-1 max-[900px]:pr-[4.25rem]";

interface RosterPlayerRowProps {
  player: LeaderboardPlayer;
  href: string;
  rankSiteLabel: (site: string) => string;
  bench?: boolean;
}

const RosterPlayerRow = ({ player, href, rankSiteLabel, bench = false }: RosterPlayerRowProps) => (
  <div
    className={`group grid min-h-[3.6rem] w-full grid-cols-[3rem_minmax(0,1.85fr)_minmax(0,1.15fr)_12rem_auto] items-center justify-items-start gap-3 border-b border-[var(--surface-card-border)] bg-[#16161f] pl-8 pr-5 text-left transition-[background] duration-150 ease-in-out hover:bg-[rgba(123,147,255,0.06)] max-[900px]:grid-cols-[2rem_minmax(0,1fr)] max-[900px]:grid-rows-[auto_auto_auto] max-[900px]:items-start max-[900px]:gap-x-[0.65rem] max-[900px]:gap-y-[0.45rem] max-[900px]:px-[0.85rem] max-[900px]:py-3 ${
      bench ? "opacity-[0.72]" : ""
    }`}
  >
    <Link to={href} className="contents text-inherit no-underline">
      <div className="flex w-12 items-center justify-center justify-self-center [&_img]:size-[18px] max-[900px]:col-start-1 max-[900px]:row-start-1 max-[900px]:self-start max-[900px]:pt-[0.45rem]">
        <PlayerRoleBadge role={player.role} small />
      </div>
      <div className={`${PLAYER_ENTITY_CLASS} gap-[0.65rem]`}>
        <ProfileIcon />
        <div className="flex min-w-0 flex-col items-start gap-[0.2rem]">
          <div className="flex min-w-0 flex-wrap items-center gap-[0.4rem]">
            <PlayerNameDisplay
              displayName={player.displayName}
              gameName={player.gameName}
              tagLine={player.tagLine}
              small
            />
            <RosterStatusBadge isActive={player.isActive} />
          </div>
        </div>
      </div>
      <div className={ROSTER_RANK_CELL_CLASS}>
        {player.error ? (
          <span className="text-[0.82rem] text-[#f87171]">{player.error}</span>
        ) : (
          <OpggRankCell tier={player.tier} rank={player.rank} leaguePoints={player.leaguePoints} />
        )}
      </div>
      <div className={WINRATE_CELL_CLASS}>
        <WinratePills wins={player.wins} losses={player.losses} />
      </div>
    </Link>
    <PlayerExternalLinks
      displayName={player.displayName}
      tagLine={player.tagLine}
      gameName={player.gameName}
      rankSiteLabel={rankSiteLabel}
      size="sm"
      className={EXTERNAL_LINKS_CLASS}
    />
  </div>
);

export const LeaderboardTabs = ({ teams, loading = false, onTabChange }: LeaderboardTabsProps) => {
  const {
    strings,
    localized,
    goTo,
    tab,
    setTab,
    expandedTeamId,
    toggleExpanded,
    playerTeamFilter,
    setPlayerTeamFilter,
    playerRoleFilter,
    setPlayerRoleFilter,
    enrichedTeams,
    players,
    showTeamOnPlayer,
    teamLogoAlt,
    rankSiteLabel,
    playerRoles,
  } = useLeaderboardTabs({ teams, onTabChange });

  if (loading) {
    return (
      <div className="w-full" aria-busy="true">
        <div className={`mx-5 my-[0.85rem] h-[52px] ${SKELETON_BLOCK}`} />
        <div className="flex flex-col gap-2 px-5 pb-5">
          {Array.from({ length: 8 }, (_, index) => (
            <div className={`h-14 ${SKELETON_BLOCK}`} key={index} />
          ))}
        </div>
      </div>
    );
  }

  if (!enrichedTeams.length) {
    return <div className="p-8 text-center text-[#8b8b9a]">{strings.LEADERBOARD.NO_TEAMS}</div>;
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-between gap-4 border-b border-[var(--surface-card-border)] px-5 py-[0.85rem] max-[900px]:flex-col max-[900px]:items-stretch max-[900px]:gap-3">
        <div className="inline-flex rounded-lg border border-[var(--surface-card-border)] bg-[#111118] p-[3px]">
          <button
            type="button"
            className={`${TOOLBAR_TAB}${tab === "players" ? " fh-ghost-btn--active-soft" : ""}`}
            onClick={() => {
              setTab("players");
            }}
          >
            {strings.LEADERBOARD.TAB_INDIVIDUAL}
          </button>
          <button
            type="button"
            className={`${TOOLBAR_TAB}${tab === "teams" ? " fh-ghost-btn--active-soft" : ""}`}
            onClick={() => {
              setTab("teams");
            }}
          >
            {strings.LEADERBOARD.TAB_TEAMS}
          </button>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3 max-[900px]:flex-col max-[900px]:items-stretch max-[900px]:justify-start">
          {tab === "players" ? (
            <>
              <label className="inline-flex items-center gap-2 max-[900px]:w-full max-[900px]:justify-between">
                <span className={TOOLBAR_LABEL}>{strings.LEADERBOARD.FILTER_TEAM}</span>
                <select
                  className={TOOLBAR_SELECT}
                  value={playerTeamFilter}
                  onChange={(event) => {
                    setPlayerTeamFilter(event.target.value);
                  }}
                >
                  <option value="all">{strings.LEADERBOARD.ALL_PLAYERS}</option>
                  {enrichedTeams.map((team) => (
                    <option key={team.teamId} value={team.teamId}>
                      {team.fullName}
                    </option>
                  ))}
                </select>
              </label>
              <label className="inline-flex items-center gap-2 max-[900px]:w-full max-[900px]:justify-between">
                <span className={TOOLBAR_LABEL}>{strings.LEADERBOARD.FILTER_ROLE}</span>
                <select
                  className={TOOLBAR_SELECT}
                  value={playerRoleFilter}
                  onChange={(event) => {
                    setPlayerRoleFilter(event.target.value);
                  }}
                >
                  <option value="all">{strings.LEADERBOARD.ALL_ROLES}</option>
                  {playerRoles.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : null}
          <span className="rounded-md border border-[var(--surface-card-border)] bg-[#111118] px-[0.7rem] py-[0.35rem] text-[0.78rem] font-semibold text-[#8b8b9a]">
            {strings.LEADERBOARD.SOLO_DUO}
          </span>
        </div>
      </div>

      <div className="w-full">
        <div className="grid grid-cols-[3rem_minmax(0,1.85fr)_minmax(0,1.15fr)_12rem] items-center gap-3 border-b border-[var(--surface-card-border)] px-5 py-[0.65rem] text-[0.72rem] font-semibold uppercase tracking-[0.06em] text-[#8b8b9a] max-[900px]:hidden">
          <span className="text-center">#</span>
          <span className="text-left">
            {tab === "teams" ? strings.LEADERBOARD.COLUMN_TEAM : strings.LEADERBOARD.COLUMN_PLAYER}
          </span>
          <span className="text-right">{strings.LEADERBOARD.COLUMN_RANK}</span>
          <span className="text-right">{strings.LEADERBOARD.COLUMN_WINRATE}</span>
        </div>

        {tab === "players"
          ? players.map((player) => {
              const playerPath = getPlayerProfilePath(player.gameName);
              const openPlayer = () => {
                goTo(playerPath);
              };

              return (
                <div
                  className="group grid min-h-[4.25rem] w-full cursor-pointer grid-cols-[3rem_minmax(0,1.85fr)_minmax(0,1.15fr)_12rem_auto] items-center justify-items-start gap-3 border-b border-[var(--surface-card-border)] bg-[var(--surface-card-row-bg)] px-5 text-left outline-none transition-[background,box-shadow] duration-150 ease-in-out hover:bg-[rgba(123,147,255,0.08)] hover:shadow-[inset_3px_0_0_#7b93ff] focus-visible:bg-[rgba(123,147,255,0.08)] focus-visible:shadow-[inset_3px_0_0_#7b93ff] max-[900px]:grid-cols-[2rem_minmax(0,1fr)] max-[900px]:grid-rows-[auto_auto_auto] max-[900px]:items-start max-[900px]:gap-x-[0.65rem] max-[900px]:gap-y-[0.45rem] max-[900px]:px-[0.85rem] max-[900px]:py-3"
                  key={`${player.teamId}#${player.gameName}#${player.tagLine}`}
                  role="link"
                  tabIndex={0}
                  onClick={(event) => {
                    if ((event.target as HTMLElement).closest("a")) return;
                    openPlayer();
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter" && event.key !== " ") return;
                    if ((event.target as HTMLElement).closest("a")) return;
                    event.preventDefault();
                    openPlayer();
                  }}
                >
                  <span
                    className={`justify-self-center text-center text-[0.95rem] max-[900px]:col-start-1 max-[900px]:row-start-1 max-[900px]:self-start max-[900px]:pt-[0.45rem] ${rankNumClass(player.position)}`}
                  >
                    {player.position}
                  </span>
                  <div className={`${PLAYER_ENTITY_CLASS} gap-3`}>
                    <ProfileIcon />
                    <div className="flex min-w-0 flex-col items-start gap-[0.2rem]">
                      <div className="flex min-w-0 flex-wrap items-center gap-[0.4rem]">
                        <PlayerNameDisplay
                          displayName={player.displayName}
                          gameName={player.gameName}
                          tagLine={player.tagLine}
                        />
                        <PlayerRoleBadge role={player.role} />
                        <RosterStatusBadge isActive={player.isActive} />
                      </div>
                      {showTeamOnPlayer ? (
                        <span className="inline-flex items-center gap-[0.35rem] text-[0.78rem] text-[#8b8b9a]">
                          <TeamLogo
                            teamId={player.teamId}
                            alt={teamLogoAlt(player.teamName)}
                            className="h-4 w-4 rounded object-cover"
                          />
                          <span>{player.teamName}</span>
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className={RANK_CELL_CLASS}>
                    {player.error ? (
                      <span className="text-[0.82rem] text-[#f87171]">{player.error}</span>
                    ) : (
                      <OpggRankCell
                        tier={player.tier}
                        rank={player.rank}
                        leaguePoints={player.leaguePoints}
                      />
                    )}
                  </div>
                  <div className={WINRATE_CELL_CLASS}>
                    <WinratePills wins={player.wins} losses={player.losses} />
                  </div>
                  <PlayerExternalLinks
                    displayName={player.displayName}
                    tagLine={player.tagLine}
                    gameName={player.gameName}
                    rankSiteLabel={rankSiteLabel}
                    size="sm"
                    className={EXTERNAL_LINKS_CLASS}
                  />
                </div>
              );
            })
          : enrichedTeams.map((team) => {
              const expanded = expandedTeamId === team.teamId;
              const activePlayers = sortPlayersByRole(
                team.players.filter((player) => player.isActive),
              );
              const benchPlayers = sortPlayersByRole(
                team.players.filter((player) => !player.isActive),
              );
              const teamWins = activePlayers.reduce((sum, p) => sum + (p.wins ?? 0), 0);
              const teamLosses = activePlayers.reduce((sum, p) => sum + (p.losses ?? 0), 0);

              return (
                <div key={team.teamId}>
                  <div
                    className="grid min-h-[4.25rem] w-full cursor-pointer grid-cols-[3rem_minmax(0,1.85fr)_minmax(0,1.15fr)_12rem] items-center justify-items-start gap-3 border-b border-[var(--surface-card-border)] bg-[var(--surface-card-row-bg)] px-5 text-left outline-none transition-[background] duration-150 ease-in-out hover:bg-[rgba(255,255,255,0.06)] focus-visible:bg-[rgba(255,255,255,0.06)] max-[900px]:grid-cols-[2.25rem_minmax(0,1fr)] max-[900px]:grid-rows-[auto_auto_auto] max-[900px]:gap-x-3 max-[900px]:gap-y-[0.55rem] max-[900px]:px-4 max-[900px]:py-[0.85rem]"
                    role="button"
                    tabIndex={0}
                    aria-expanded={expanded}
                    onClick={(event) => {
                      if ((event.target as HTMLElement).closest("a")) return;
                      toggleExpanded(team.teamId);
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter" && event.key !== " ") return;
                      if ((event.target as HTMLElement).closest("a")) return;
                      event.preventDefault();
                      toggleExpanded(team.teamId);
                    }}
                  >
                    <span
                      className={`justify-self-center text-center text-[0.95rem] max-[900px]:self-start max-[900px]:pt-[0.35rem] max-[900px]:[grid-row:1/span_2] ${rankNumClass(team.position)}`}
                    >
                      {team.position}
                    </span>
                    <div className="flex w-full min-w-0 items-center justify-start justify-self-start gap-3 max-[900px]:col-start-2">
                      <TeamLogo
                        teamId={team.teamId}
                        alt={teamLogoAlt(team.fullName)}
                        className="h-10 w-10 shrink-0 rounded-[10px] bg-[#2a2a36] object-cover"
                      />
                      <div className="flex min-w-0 flex-col items-start gap-[0.2rem]">
                        <span className="overflow-hidden text-ellipsis whitespace-nowrap text-left text-[0.98rem] font-bold text-[#e8c872]">
                          {team.fullName}
                        </span>
                      </div>
                    </div>
                    <div className="w-[10.75rem] justify-self-end max-[900px]:col-start-2 max-[900px]:w-auto max-[900px]:justify-self-start">
                      <OpggRankCell
                        tier={placementTier(team.position, enrichedTeams.length)}
                        leaguePoints={team.totalScore}
                        showDivision={false}
                      />
                    </div>
                    <div className="justify-self-end max-[900px]:col-start-2">
                      <WinratePills wins={teamWins} losses={teamLosses} />
                    </div>
                  </div>

                  {expanded ? (
                    <div className="border-b border-[var(--surface-card-border)] bg-[#14141c]">
                      <div className="pb-[0.35rem] pl-8 pr-5 pt-[0.55rem] text-[0.72rem] font-bold uppercase tracking-[0.06em] text-[#8b8b9a]">
                        {strings.LEADERBOARD.ACTIVE_ROSTER}
                      </div>
                      {activePlayers.map((player) => (
                        <RosterPlayerRow
                          key={`${team.teamId}#${player.gameName}#${player.tagLine}`}
                          player={player}
                          href={localized(getPlayerProfilePath(player.gameName))}
                          rankSiteLabel={rankSiteLabel}
                        />
                      ))}

                      {benchPlayers.length > 0 ? (
                        <>
                          <div className="pb-[0.35rem] pl-8 pr-5 pt-[0.85rem] text-[0.72rem] font-bold uppercase tracking-[0.06em] text-[#8b8b9a]">
                            {strings.LEADERBOARD.SUBSTITUTES}
                          </div>
                          {benchPlayers.map((player) => (
                            <RosterPlayerRow
                              key={`${team.teamId}#${player.gameName}#${player.tagLine}`}
                              player={player}
                              href={localized(getPlayerProfilePath(player.gameName))}
                              rankSiteLabel={rankSiteLabel}
                              bench
                            />
                          ))}
                        </>
                      ) : null}

                      {team.opggUrl ? (
                        <a
                          className="mx-5 mb-[0.85rem] mt-2 inline-flex rounded-md bg-[rgba(123,147,255,0.12)] px-[0.65rem] py-[0.4rem] text-[0.82rem] font-semibold text-[#7b93ff] no-underline hover:bg-[rgba(123,147,255,0.18)] hover:no-underline"
                          href={team.opggUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {strings.LEADERBOARD.VIEW_OPGG}
                        </a>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
      </div>
    </div>
  );
};
